import { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const KINDS = [
  { id: "services", label: "Services" },
  { id: "ai_solutions", label: "AI Solutions" },
  { id: "domains", label: "Expertise (Domains)" },
];

const BLANK = { title: "", description: "", icon: "Sparkles", order: 0, status: "published", is_active: true };

export default function AdminContentLists() {
  const [kind, setKind] = useState(KINDS[0].id);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = (k = kind) => {
    setLoading(true);
    api.get(`/content-lists/${k}`)
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(kind); }, [kind]);

  const save = async () => {
    if (!draft?.title) return toast.error("Title is required.");
    setSaving(true);
    try {
      const body = { ...draft };
      delete body.id;
      if (draft.id) await api.put(`/content-lists/${kind}/${draft.id}`, body);
      else await api.post(`/content-lists/${kind}`, body);
      toast.success("Saved.");
      setDraft(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/content-lists/${kind}/${id}`);
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="admin-content-lists" className="max-w-5xl">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#2563EB]">Site Content</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A] mt-2">Services · AI Solutions · Expertise</h1>
        <p className="text-[#475569] mt-2 text-sm">Edit the card lists for the public homepage. Use Lucide icon names like <code>Sparkles</code>, <code>Cloud</code>, <code>BrainCircuit</code>.</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-blue-100 mb-6">
        {KINDS.map((k) => (
          <button
            key={k.id}
            onClick={() => { setKind(k.id); setDraft(null); }}
            data-testid={`kind-${k.id}`}
            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 ${
              kind === k.id ? "border-[#2563EB] text-[#1D4ED8]" : "border-transparent text-[#475569] hover:text-[#2563EB]"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setDraft({ ...BLANK, order: (items.length || 0) + 1 })}
        data-testid="add-item"
        className="mb-4 inline-flex items-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold"
      >
        <Plus size={14} /> Add item
      </button>

      {draft && (
        <div className="mb-6 bg-white border border-blue-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg text-[#0F172A]">{draft.id ? "Edit" : "New"} item</h3>
            <button onClick={() => setDraft(null)} className="text-[#94A3B8] hover:text-[#0F172A]"><X size={16} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Inp label="Title *" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="d-title" />
            <Inp label="Icon (lucide name)" value={draft.icon} onChange={(v) => setDraft({ ...draft, icon: v })} testid="d-icon" />
            <div className="md:col-span-2">
              <Inp label="Description" textarea value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} testid="d-desc" />
            </div>
            <Inp label="Order" type="number" value={draft.order} onChange={(v) => setDraft({ ...draft, order: parseInt(v || "0", 10) })} testid="d-order" />
            <label className="flex items-center gap-2 text-sm text-[#0F172A] mt-6">
              <input type="checkbox" checked={!!draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} data-testid="d-active" />
              Active (shown on site)
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="d-save"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`item-${it.id}`} className="bg-white border border-blue-100 rounded-2xl p-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-[#475569]">
                  <Sparkles size={12} className="text-[#2563EB]" /> {it.icon || "—"} · order {it.order}
                </div>
                <div className="font-display text-lg text-[#0F172A] mt-1 truncate">{it.title}</div>
                {it.description && <div className="text-sm text-[#475569] mt-1 line-clamp-2">{it.description}</div>}
              </div>
              <div className="flex flex-col gap-1.5">
                <button onClick={() => setDraft({ ...it })} className="text-xs px-3 py-1.5 rounded-md border border-blue-200 hover:border-[#2563EB] hover:text-[#2563EB] text-[#0F172A]">Edit</button>
                <button onClick={() => remove(it.id)} className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center justify-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Inp({ label, value, onChange, testid, type = "text", textarea }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#0F172A] mb-1.5">{label}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid}
          className="w-full px-3 py-2 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm" />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full px-3 py-2 rounded-lg border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm" />
      )}
    </label>
  );
}
